import { roleAlignmentScore } from '../../../../modules/recommendations/engine/scoring/dimensionScores';
import type { Job } from '../JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RankedJob = Job & {
  /** Normalized 0-100 match score (not raw token count). */
  matchScore: number;
  /** Skill names from the job that the user already has. */
  matchedSkills: string[];
  /** Required skills the user is missing. */
  missingSkills: string[];
};

// ---------------------------------------------------------------------------
// Experience level helpers
// ---------------------------------------------------------------------------

/**
 * Convert a free-text experience_level string to a numeric seniority bucket:
 *   0 = Fresher / Intern
 *   1 = Entry / 0-1 yr
 *   2 = Junior / 1-3 yrs
 *   3 = Mid    / 3-6 yrs
 *   4 = Senior / 6-10 yrs
 *   5 = Lead / Principal / 10+ yrs
 * Returns null when the string cannot be mapped.
 */
export function experienceBucket(level: string | null | undefined): number | null {
  if (!level) return null;
  const l = level.toLowerCase().replace(/[^a-z0-9\s+]/g, ' ').trim();
  if (/fresher|intern|trainee|0\s*[-–]\s*0/.test(l)) return 0;
  if (/entry|0\s*[-–]\s*1/.test(l))                   return 1;
  if (/junior|jr|1\s*[-–]\s*[23]/.test(l))            return 2;
  if (/mid|2\s*[-–]\s*[456]|3\s*[-–]\s*[56]|3\+|2\+/.test(l))  return 3;
  if (/senior|sr|5\s*\+|6\s*[-–]|7\s*[-–]|8\s*[-–]|9\s*[-–]/.test(l)) return 4;
  if (/lead|principal|staff|architect|10\s*\+|director|head/.test(l))     return 5;
  return null;
}

/**
 * Convert years of experience to a seniority bucket.
 */
export function yearsToBucket(years: number): number {
  if (years <= 0)  return 0;
  if (years <= 1)  return 1;
  if (years <= 3)  return 2;
  if (years <= 6)  return 3;
  if (years <= 10) return 4;
  return 5;
}

/**
 * Compute a 0–1 experience fit multiplier.
 * A severe under-qualification significantly drops the score;
 * overqualification applies a modest penalty.
 */
function experienceFitMultiplier(
  userBucket: number | null,
  jobBucket:  number | null,
): number {
  if (userBucket === null || jobBucket === null) return 0.90; // unknown → slight uncertainty
  const gap = jobBucket - userBucket; // positive = user is under-qualified
  if (gap >= 4) return 0.25;
  if (gap === 3) return 0.40;
  if (gap === 2) return 0.60;
  if (gap === 1) return 0.82;
  if (gap === 0) return 1.00; // exact match
  if (gap === -1) return 0.95; // slightly overqualified
  if (gap === -2) return 0.85; // overqualified
  return 0.75;                  // heavily overqualified
}

// ---------------------------------------------------------------------------
// Title stop-words — generic role-level words that should NOT trigger a title
// match on their own.  "Digital Marketing Manager" vs "Product Manager" both
// share "manager" — without this guard that becomes a spurious +10 bonus.
// ---------------------------------------------------------------------------

const TITLE_STOP_WORDS = new Set([
  'manager', 'senior', 'junior', 'specialist', 'executive', 'officer',
  'head', 'lead', 'associate', 'analyst', 'director', 'coordinator',
  'consultant', 'advisor', 'intern', 'trainee', 'fresher', 'staff',
  'principal', 'architect', 'vp', 'svp', 'evp',
]);

// ---------------------------------------------------------------------------
// Canonicalization
// ---------------------------------------------------------------------------

const SYNONYMS: Record<string, string> = {
  js:         'javascript',
  ts:         'typescript',
  reactjs:    'react',
  nodejs:     'node',
  py:         'python',
  postgres:   'postgresql',
  k8s:        'kubernetes',
  tf:         'terraform',
  ml:         'machine learning',
  ai:         'artificial intelligence',
  llm:        'large language models',
  'c++':      'cpp',
  'c#':       'csharp',
};

/**
 * Lower-case, strip non-alphanumeric (except + and # for e.g. C++/C#),
 * split on whitespace/comma, drop tokens < 2 chars, apply synonym map.
 * Exported so useUserSkills can reuse the exact same normalisation.
 */
export function tokenize(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s,+#]/g, ' ')
      .split(/[\s,]+/)
      .map(t => t.trim())
      .filter(t => t.length >= 2)
      .map(t => SYNONYMS[t] ?? t),
  );
}

// ---------------------------------------------------------------------------
// rankJobs — normalized 0-100 output
// ---------------------------------------------------------------------------

/**
 * Sort jobs by how well they match the user's skill/title/experience context.
 *
 * Scoring (0-100):
 *   1. Skill score  = (matched / required) × 100  → weight 0.65
 *   2. Domain title match: job title domain tokens overlap user's title tokens,
 *      skipping generic stop-words ("manager", "senior", etc.) → +10 bonus
 *   3. Role/function alignment via classifyJobFunction (Product, Marketing,
 *      Engineering, …) — if BOTH sides are classifiable the score is blended
 *      in (weight 0.25).  Cross-domain mismatches (PM vs Marketing) are further
 *      capped at 42 to prevent them appearing as "Good fit".
 *   4. Experience-fit multiplier applied on top (Fresher→Senior = ×0.40).
 *   5. Recency tiebreak for equal scores.
 *
 * When a job lists no required skills, a neutral score 50 is assigned.
 */
export function rankJobs(
  jobs: Job[],
  ctx: {
    skills: Set<string>;
    titles: Set<string>;
    experienceYears?: number | null;
    /** Raw headline / current job title — used for cross-domain role alignment. */
    headline?: string | null;
  },
): RankedJob[] {
  const userBucket = ctx.experienceYears != null
    ? yearsToBucket(ctx.experienceYears)
    : null;

  // Pre-classify the user's career function once (not per job)
  const userHeadline = ctx.headline ?? null;

  return jobs
    .map(j => {
      // Prefer must_have_skills as requirements; fall back to skills array
      const mustHave  = j.must_have_skills ?? [];
      const allSkills = j.skills ?? [];
      const required  = mustHave.length > 0 ? mustHave : allSkills.slice(0, 10);

      const matchedSkills: string[] = [];
      const missingSkills: string[] = [];

      for (const skill of required) {
        const tokens = tokenize(skill);
        let found = false;
        for (const t of tokens) {
          if (ctx.skills.has(t)) { found = true; break; }
        }
        if (found) matchedSkills.push(skill);
        else        missingSkills.push(skill);
      }

      // 1. Skill component (0-100)
      const skillPct = required.length > 0
        ? Math.round((matchedSkills.length / required.length) * 100)
        : 50; // neutral when no skills declared

      // 2. Domain title match — skip generic stop-words so "manager" in
      //    "Digital Marketing Manager" doesn't match "Product Manager".
      const jobTitleTokens = tokenize(j.title);
      let titleDomainMatch = false;
      for (const t of jobTitleTokens) {
        if (TITLE_STOP_WORDS.has(t)) continue; // skip generic level words
        if (ctx.titles.has(t)) { titleDomainMatch = true; break; }
      }

      // 3. Role / function alignment
      //    roleAlignmentScore returns 50 (neutral) if either side is 'unknown',
      //    so only classifiable cross-domain pairs are penalised.
      const roleScore = roleAlignmentScore(userHeadline, undefined, j.title);
      const roleIsClassified = roleScore !== 50; // 50 = neutral / unknown

      // Blend: skills 65% + title bonus 10 + role 25% (only when classifiable)
      let blendedScore: number;
      if (roleIsClassified) {
        blendedScore = skillPct * 0.65 + (titleDomainMatch ? 10 : 0) + roleScore * 0.25;
        // Hard cap for clear cross-domain mismatches (e.g. PM vs Digital Marketing)
        if (roleScore < 30) blendedScore = Math.min(blendedScore, 42);
      } else {
        // Role unknown on either side → fall back to skills-heavy formula
        blendedScore = skillPct * 0.75 + (titleDomainMatch ? 10 : 0) + 15;
      }

      // 4. Experience multiplier — penalise under/over qualification
      const jobBucket = experienceBucket(j.experience_level);
      const expMult   = experienceFitMultiplier(userBucket, jobBucket);

      const score = Math.min(100, Math.round(blendedScore * expMult));

      return {
        ...j,
        matchScore:    score,
        matchedSkills: matchedSkills.slice(0, 5),
        missingSkills: missingSkills.slice(0, 5),
      };
    })
    .sort(
      (a, b) =>
        b.matchScore - a.matchScore ||
        (b.created ?? '').localeCompare(a.created ?? ''),
    );
}
